import { useId, useRef } from 'react';

import { formatDate } from '@betterment/core';
import { Box } from '@betterment/samba-react';
import { token } from '@betterment/samba-styled-system/tokens';
import {
  ChartProvider,
  Cursor,
  Grid,
  HiddenChartDescription,
  LineSeries,
  PlaceholderAreaChart,
  XAxis,
  XYChart,
  YAxis,
  useXYScales,
} from '@betterment/shared-components';
import { ParentSize } from '@visx/responsive';
import { isToday } from 'date-fns';
import { isNil } from 'es-toolkit/compat';

import { trackHover } from '../../../../features/track';
import { PerformanceHistoryTimeWeightedReturnItem } from '../../../../graphql/schema';

export type TimeWeightedReturnsChartProps = {
  timeSeries?: PerformanceHistoryTimeWeightedReturnItem[];
  benchmarkTimeSeries?: PerformanceHistoryTimeWeightedReturnItem[];
};

const formatDateLabel = (value: Date) => {
  return isToday(value) ? 'Today' : formatDate(value, 'longOrdinalShortMonth');
};

const yTickFormat = (value: number) => {
  return `${value.toFixed(0)}%`;
};

const formatReturnsDataPointLabel = (datum: PerformanceHistoryTimeWeightedReturnItem): string => {
  if (!datum) return '';

  const dateStr = formatDate(datum.date, 'longOrdinalShortMonth');
  const returnStr = isNil(datum.cumulativeReturn) ? '--' : yTickFormat(datum.cumulativeReturn);

  return `${dateStr}, Return: ${returnStr}`;
};

const TimeWeightedReturnsChartDescription: React.FC<{
  id: string;
  startDate: Date;
  endDate: Date;
  startingReturn: number;
  endingReturn: number;
  minReturn: number;
  maxReturn: number;
}> = ({ id, startDate, endDate, startingReturn, endingReturn, minReturn, maxReturn }) => {
  const change = endingReturn - startingReturn;
  let direction: string;
  if (change > 0) {
    direction = 'increased';
  } else if (change < 0) {
    direction = 'decreased';
  } else {
    direction = 'remained unchanged';
  }

  return (
    <HiddenChartDescription id={id}>
      Time-weighted returns from {formatDate(startDate)} to {formatDate(endDate)}.{'\n'}Returns {direction} from{' '}
      {yTickFormat(startingReturn)} to {yTickFormat(endingReturn)}.{'\n'}Range: {yTickFormat(minReturn)} to{' '}
      {yTickFormat(maxReturn)}.
    </HiddenChartDescription>
  );
};

export const TimeWeightedReturnsChart: React.FC<TimeWeightedReturnsChartProps> = ({
  timeSeries,
  benchmarkTimeSeries,
}) => {
  const describeId = useId();
  const hasTrackedHover = useRef(false);

  const handleChartHover = () => {
    if (!hasTrackedHover.current) {
      trackHover({
        name: 'twrGraphScrub',
        module: 'Performance',
        location: 'TimeWeightedReturns',
      });
      hasTrackedHover.current = true;
    }
  };

  const portfolioValues = timeSeries?.map(d => d.cumulativeReturn ?? 0) ?? [];
  const benchmarkValues = benchmarkTimeSeries?.map(d => d.cumulativeReturn ?? 0) ?? [];
  const allValues = [...portfolioValues, ...benchmarkValues];

  const dataMin = allValues.length > 0 ? Math.min(...allValues) : 0;
  const dataMax = allValues.length > 0 ? Math.max(...allValues) : 0;

  const validEntries = timeSeries?.filter(d => d.cumulativeReturn != null) ?? [];
  const firstEntry = validEntries[0];
  const lastEntry = validEntries[validEntries.length - 1];

  const { xScale, yScale } = useXYScales({
    yMin: dataMin,
    yMax: dataMax,
  });

  const benchmarkDataKey = 'benchmark';
  const benchmarkColor = token('colors.dataviz.purple.2');
  const returnsDataKey = 'returns';
  const returnsColor = token('colors.dataviz.blue.2');
  const cursorColors = [returnsColor];
  if (benchmarkTimeSeries && benchmarkTimeSeries.length > 0) {
    cursorColors.push(benchmarkColor);
  }

  return (
    <Box css={{ height: '350px' }} onMouseEnter={handleChartHover}>
      <ParentSize debounceTime={10}>
        {({ width }) =>
          timeSeries && timeSeries.length > 0 ? (
            <>
              <TimeWeightedReturnsChartDescription
                id={describeId}
                startDate={firstEntry.date}
                endDate={lastEntry.date}
                startingReturn={firstEntry.cumulativeReturn!}
                endingReturn={lastEntry.cumulativeReturn!}
                minReturn={dataMin}
                maxReturn={dataMax}
              />
              <ChartProvider xScale={xScale} yScale={yScale} theme={{ colors: cursorColors }}>
                <XYChart
                  width={width}
                  height={350}
                  margin={{ right: 0, top: 20, left: 50, bottom: 50 }}
                  aria-label="Time-weighted returns chart"
                  aria-describedby={describeId}
                  xScale={xScale}
                  yScale={yScale}
                >
                  <Grid />
                  <XAxis tickFormat={formatDateLabel} />
                  <YAxis tickFormat={yTickFormat} />
                  <LineSeries
                    dataKey={returnsDataKey}
                    data={timeSeries}
                    variant="solid"
                    color={returnsColor}
                    xAccessor={d => new Date(d.date)}
                    yAccessor={d => d.cumulativeReturn}
                  />
                  {benchmarkTimeSeries && benchmarkTimeSeries.length > 0 && (
                    <LineSeries
                      dataKey={benchmarkDataKey}
                      data={benchmarkTimeSeries}
                      variant="solid"
                      color={benchmarkColor}
                      xAccessor={d => new Date(d.date)}
                      yAccessor={d => d.cumulativeReturn}
                    />
                  )}
                  <Cursor formatDataPointLabel={formatReturnsDataPointLabel} />
                </XYChart>
              </ChartProvider>
            </>
          ) : (
            <PlaceholderAreaChart
              width={width}
              height={350}
              variant={!timeSeries ? 'loading' : 'empty'}
              aria-describedby={describeId}
              aria-label={!timeSeries ? 'Loading chart' : 'No data available'}
            />
          )
        }
      </ParentSize>
    </Box>
  );
};
